/**
 * Remove every locally persisted copy of the signed-in user's data (GH #1909).
 *
 * Runs on logout so the next person on a shared device inherits neither the
 * service worker's cached API responses nor the IndexedDB stores (offline
 * mutation queue, per-entity YDoc stores, pending uploads).
 */
import { logger } from '@tunasong/models'

/** Message type the service worker handles by deleting its per-user caches. Mirrored in the app's `sw.ts`. */
export const PURGE_USER_DATA_MESSAGE = 'PURGE_USER_DATA'

/** IndexedDB databases that hold device settings rather than user data. */
const preservedDatabases = ['device']

/** Used when the browser cannot enumerate its databases (`indexedDB.databases()` is missing). */
const knownUserDatabases = ['tunasong-mutations', 'entity-uploads']

/** Resolve with `fallback` if `promise` has not settled within `ms`. */
const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T) =>
  new Promise<T>(resolve => {
    const timer = setTimeout(() => resolve(fallback), ms)
    void promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(fallback)
      }
    )
  })

const purgeServiceWorkerCaches = async (timeoutMs: number) => {
  const serviceWorker = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker
  if (!serviceWorker) {
    return
  }
  const registration = await withTimeout(serviceWorker.ready, timeoutMs, null)
  const worker = registration?.active
  if (!worker) {
    logger.warn('purgeUserData: no active service worker; its caches were not purged')
    return
  }
  const channel = new MessageChannel()
  const acknowledged = new Promise<boolean>(resolve => {
    channel.port1.onmessage = () => resolve(true)
  })
  worker.postMessage({ type: PURGE_USER_DATA_MESSAGE }, [channel.port2])
  if (!(await withTimeout(acknowledged, timeoutMs, false))) {
    logger.warn('purgeUserData: the service worker did not acknowledge the cache purge')
  }
  channel.port1.close()
}

/**
 * Resolves when the database is gone. A blocked delete completes once the
 * blocking connection closes; `purgeUserData`'s timeout bounds the wait.
 */
const deleteDatabase = (name: string) =>
  new Promise<void>(resolve => {
    const request = indexedDB.deleteDatabase(name)
    request.onsuccess = () => resolve()
    request.onerror = () => {
      logger.warn(`purgeUserData: could not delete IndexedDB database ${name}`, request.error)
      resolve()
    }
    request.onblocked = () => {
      logger.warn(`purgeUserData: deleting IndexedDB database ${name} is blocked by an open connection`)
    }
  })

const purgeIndexedDB = async () => {
  if (typeof indexedDB === 'undefined') {
    return
  }
  const names =
    typeof indexedDB.databases === 'function'
      ? (await indexedDB.databases()).flatMap(db => (db.name ? [db.name] : []))
      : knownUserDatabases
  await Promise.all(names.filter(name => !preservedDatabases.includes(name)).map(deleteDatabase))
}

/** Never rejects and never waits longer than `timeoutMs`, so logout always proceeds. */
export const purgeUserData = async ({ timeoutMs = 3000 }: { timeoutMs?: number } = {}): Promise<void> => {
  const purge = Promise.all([purgeServiceWorkerCaches(timeoutMs), purgeIndexedDB()]).then(
    () => undefined,
    e => logger.error('purgeUserData failed', e)
  )
  await withTimeout(purge, timeoutMs, undefined)
}
